// @flow
import * as React from 'react';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

// Components
import { Container, Row, Col } from 'reactstrap';
import ContentLoader from 'react-content-loader';
import LoaderHOC from 'components/atoms/LoaderHOC';
import DataSlot from 'components/atoms/DataSlot';
import AppointmentFlowWizard from 'components/templates/AppointmentFlowWizard/';
import AppointmentFlowHeader from 'components/molecules/AppointmentFlowHeader/';
import AppointmentFlowFooter from 'components/molecules/AppointmentFlowFooter/';
import FatalError from 'components/api/components/FatalError/FatalError';

// CTA Components
import CTAFlowHeader from 'components/molecules/CTAFlowHeader';
import CtaStepIndicator from 'components/atoms/CtaStepIndicator';

// Constants
import {
  SERVICES_ROUTE,
  INDIVIDUAL_SERVICES_ROUTE,
  DROPOFF_ROUTE,
  APPOINTMENT_ROUTE,
  PICKUP_ROUTE,
  TRANSPORTATION_ROUTE
} from 'constants/routes';
import { NAVIGATION } from 'constants/navigations';
import { TRANSPORTATION } from 'constants/transportation';
import { labelsStepIndicator } from 'constants/CTAConfig';

import './TransportationPage.scss';

// Types
import { gtmUserEventType } from 'types/gtm';
import type { CTA } from 'api/xws/optimizely/optimizely';

const { FORWARD } = NAVIGATION;
const {
  VALET,
  DROPOFF,
  VALETLOANER,
  SHUTTLE,
  WAITER,
  LOANER,
  RENTAL,
  RIDESHARE
} = TRANSPORTATION;

const ns = 'transportation-page';

export type Props = {
  goTo: (path: string, direction: string) => void,
  getTransportationOptions: () => void,
  transportations: () => Object,
  setTransportationOption: (transportation: string) => Object,
  transportation: string,
  transportationOption: string,
  setTransportationInfo: (transportation: Object) => void,
  intl: IntlShape,
  isValetAvailable: Boolean,
  previousPath: String,
  currentPath: String,
  noAdvisorsError: Boolean,
  ctaBanner: CTA | null,
  gtmUserEvent: gtmUserEventType,
  showProgressBar: boolean,
  hideBottomCTA: boolean,
  sendTransportationOptionsShown: () => void,
  sendTransportationOptionsStart: () => void
};

type Transportation = VALET | DROPOFF;

type State = {
  showModal: boolean
};

class TransportationPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    (this: any).noAdvisorsErrorPopup = React.createRef();
    this.state = {
      showModal: false
    };
  }

  componentDidMount() {
    this.props.getTransportationOptions();
  }

  componentDidUpdate() {
    const {
      noAdvisorsError,
      previousPath,
      currentPath,
      isValetAvailable
    } = this.props;
    const { showModal } = this.state;

    if (showModal === false) {
      const validPath =
        currentPath === TRANSPORTATION_ROUTE &&
        (SERVICES_ROUTE === previousPath ||
          INDIVIDUAL_SERVICES_ROUTE === previousPath);

      if (validPath && noAdvisorsError && isValetAvailable) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ showModal: true });
        (this: any).noAdvisorsErrorPopup.current.setShowModal(true);
      }
    }
  }

  redirectTo(typeOfTransportation: Transportation) {
    const {
      goTo,
      transportations,
      setTransportationOption,
      setTransportationInfo
    } = this.props;

    const transportWithoutValet = transportations.filter(
      ({ data }) => data !== VALET
    );
    if (typeOfTransportation === VALET) {
      setTransportationInfo(transportations.find(({ data }) => data === VALET));
      goTo(PICKUP_ROUTE, FORWARD);
    } else {
      if (transportWithoutValet.length > 1) {
        goTo(DROPOFF_ROUTE, FORWARD);
      } else {
        transportWithoutValet.map(({ data }) => setTransportationOption(data));
        goTo(APPOINTMENT_ROUTE, FORWARD);
      }
    }
  }

  onBackButtonClick = () => {
    const {
      transportationOption,
      transportations,
      setTransportationInfo
    } = this.props;
    const selectedTransportation = transportations.find(
      ({ data }) => data === transportationOption
    );
    if (selectedTransportation) {
      setTransportationInfo(selectedTransportation);
    }
  };

  showPanelPickUp() {
    const { transportation, intl } = this.props;
    return (
      <Container className={ns}>
        <FatalError
          key="fatalError"
          ref={(this: any).noAdvisorsErrorPopup}
          noAdvisorsError={true}
        />
        <LoaderHOC
          serviceId={'metaVehicle|apptOptions'}
          placeHolderType={() => (
            <ContentLoader height={30}>
              <rect x="0" y="0" className={`${ns}__placeholder-rect-title`} />
            </ContentLoader>
          )}
          component={() => (
            <Row>
              <Col className={'d-flex flex-column'}>
                <h1 className={`${ns}__title`}>
                  {intl.formatMessage({
                    id:
                      'c8.transportation.subtitle.are.you.dropping.off.or.want.us.to.pickup.your.vehicle'
                  })}
                </h1>
              </Col>
            </Row>
          )}
        />
        <LoaderHOC
          serviceId={'metaVehicle|apptOptions'}
          placeHolderType={() => (
            <ContentLoader height={180}>
              <rect x="0" y="40" className={`${ns}__placeholder-rect`} />
            </ContentLoader>
          )}
          component={() => (
            <Row>
              <Col>
                <DataSlot
                  value={intl.formatMessage({
                    id: 'c8.transportation.i.will.dropoff.my.vehicle'
                  })}
                  bsStyle="primary"
                  onClick={() => this.redirectTo(DROPOFF)}
                  active={
                    DROPOFF === transportation ||
                    SHUTTLE === transportation ||
                    WAITER === transportation ||
                    LOANER === transportation ||
                    RENTAL === transportation ||
                    RIDESHARE === transportation
                  }
                />
                <DataSlot
                  value={intl.formatMessage({
                    id: 'c8.transportation.pickup.my.vehicle'
                  })}
                  bsStyle="primary"
                  onClick={() => {
                    this.redirectTo(VALET);
                  }}
                  active={
                    VALET === transportation || VALETLOANER === transportation
                  }
                />
              </Col>
            </Row>
          )}
        />
      </Container>
    );
  }

  render() {
    const {
      transportation,
      goTo,
      intl,
      ctaBanner,
      gtmUserEvent,
      showProgressBar,
      hideBottomCTA
    } = this.props;
    let route;
    return (
      <AppointmentFlowWizard
        hideFooter={Boolean(hideBottomCTA)}
        header={() => {
          return ctaBanner !== null ? (
            <CTAFlowHeader
              label={intl.formatMessage({ id: 'c8.header.services' })}
              hideServices={false}
              routeTo={SERVICES_ROUTE}
              backButtonAriaLabel={intl.formatMessage({
                id: 'c8.aria.back.to.service.options',
                defaultMessage: 'Back to service options button'
              })}
              ctaButtonDisabled={!transportation}
              ctaBanner={ctaBanner}
              onClick={this.onBackButtonClick}
              onClickCtaButton={() => {
                if (
                  transportation === VALET ||
                  transportation === VALETLOANER
                ) {
                  route = PICKUP_ROUTE;
                } else {
                  route = DROPOFF_ROUTE;
                }
                gtmUserEvent(
                  'NEXT button',
                  'Clicked',
                  `Go to next page through CTA`,
                  `GTM_CURRENT_PAGE > ${route} page`
                );
                goTo(route, FORWARD);
              }}
              typeButton={'cta-next'}
            />
          ) : (
            <AppointmentFlowHeader
              label={intl.formatMessage({ id: 'c8.header.services' })}
              routeTo={SERVICES_ROUTE}
              onClick={this.onBackButtonClick}
              backButtonAriaLabel={intl.formatMessage({
                id: 'c8.aria.back.to.service.options',
                defaultMessage: 'Back to service options button'
              })}
            />
          );
        }}
        footer={() => {
          return !hideBottomCTA && transportation ? (
            <AppointmentFlowFooter
              buttonDisabled={false}
              buttonText={intl.formatMessage({ id: 'c8.footer.next' })}
              onClick={() => {
                if (
                  transportation === VALET ||
                  transportation === VALETLOANER
                ) {
                  goTo(PICKUP_ROUTE, FORWARD);
                } else {
                  goTo(DROPOFF_ROUTE, FORWARD);
                }
              }}
            />
          ) : null;
        }}
        body={() => (
          <React.Fragment>
            {showProgressBar && (
              <CtaStepIndicator
                htmlId="VehiclePageStepIndicator"
                labels={labelsStepIndicator(intl)}
                activeStep={3}
              />
            )}
            {this.showPanelPickUp()}
          </React.Fragment>
        )}
      />
    );
  }
}

export default TransportationPage;



// WEBPACK FOOTER //
// ./src/components/pages/TransportationPage/TransportationPage.js